// @ts-strict-ignore
/** @jsx jsx */
/** @jsxFrag null */
import {jsx} from 'panel';

import {SelectTheme} from 'common/components/select/constants';
import {SelectEl} from 'common/components/select';
import {ModalEl} from 'common/components/modal';
import {SpinnerEl} from 'common/components/spinner';
import {TEXT_HIGHLIGHT_THEME, TextHighlightEl} from 'common/components/text-highlight';
import {TooltipEl} from 'common/components/tooltip';
import {ButtonTheme} from 'common/components/button/constants';
import {ShareOptionsLocation, SharingModalState, SharingModalViewType} from 'common/widgets/sharing/modal/types';
import {truncate} from 'common/util/string';
import {BannerEl} from 'common/components/banner';
import {BannerAction, BannerTheme, BannerType} from 'common/components/banner/types';
import {ButtonEl} from 'common/components/button';
import {SwitchEl} from 'common/components/switch';
import {TableEl} from 'common/components/table';
import {LoadingStatus} from 'common/widgets/sharing/types';
import {LinkDetailMixin} from 'common/templates/public-dashboards/mixins';
import {Placement} from 'common/components/tooltip/types';
import {currentProjectStore} from 'common/report/stores/project-details';
import {SvgIconEl} from 'common/components/svg-icon';

import TableStyle from './public-links-table.inline.styl';
import {ShareSection} from './helpers';
import type {SharingModal} from './index';
import {UserSearchTagInputEl} from './user-search-tag-input';
import {PublicBoardBannerEl} from './public-board-banner';

const PublicDashboardDetailsView = ({
  state,
  helpers,
  showSuperadminBanner,
}: {
  state: SharingModalState;
  helpers: any;
  showSuperadminBanner: boolean;
}) => {
  return (
    <div sel='.wrapper' class={{'no-min-height': helpers.shouldShowUpsellBanner()}}>
      <header sel='.modal-header'>{helpers.t(`components.sharing.boards.public-board-details`)}</header>
      <div sel='.content'>
        <PublicBoardBannerEl
          showSuperadminBanner={showSuperadminBanner}
          publicDashboardLinks={state.publicDashboardLinks}
          boardName={helpers.getEntityName()}
        />
        <TableEl
          sel='.mp-table'
          attrs={{
            elref: `publicDashboardLinksTable`,
            'empty-message': helpers.t(`components.sharing.boards.no-public-urls`),
            'style-override': TableStyle,
            'track-name': `Public Dashboard Links`,
            'window-scroll': true,
            'max-body-height': `450px`,
            'disable-clickable-rows': true,
            'disable-expandable-rows': true,
            loading: state.publicDashboardLinks.loading === LoadingStatus.Pending,
          }}
          props={{
            rows: helpers.publicDashboard.getPublicDashboardLinksTableRows(),
            columns: helpers.publicDashboard.getPublicDashboardLinksTableCols(),
            rowDetailsRenderer: LinkDetailMixin(helpers.publicDashboard.handlePasswordChange),
          }}
          on={{
            rowDetailsVisibilityChange: helpers.publicDashboard.handleRowDetailsVisibilityChange,
          }}
        />
      </div>
      <footer sel='.modal-actions'>
        <ButtonEl
          sel='.public-dashboard-back-button'
          attrs={{
            elref: `publicDashboardDetailsBackButton`,
            theme: ButtonTheme.Secondary,
          }}
          on={{
            click: () => helpers.publicDashboard.updateSharingModalViewType(SharingModalViewType.Sharing),
          }}
        >
          {helpers.t(`common.back`)}
        </ButtonEl>
        <ButtonEl
          sel='.public-dashboard-done-button'
          attrs={{
            theme: ButtonTheme.Primary,
          }}
          on={{
            click: helpers.publicDashboard.handlePublicDashboardDoneClick,
          }}
        >
          {helpers.t(`common.done`)}
        </ButtonEl>
      </footer>
    </div>
  );
};

export function template(this: SharingModal) {
  const {
    pendingCanEdit,
    pendingGrants,
    searchText,
    showCopyLinkText,
    showPopover,
    publicDashboardEnabled,
    modalViewType,
  } = this.state;
  const projectMemberCount = this.helpers.getProjectMemberCount();
  const showDisabledBanner =
    this.attr(`disabled`) && this.attr(`disabled-reason`) && !this.helpers.shouldShowUpsellBanner();
  const showSuperadminBanner = this.attr(`is-superadmin`) && !this.helpers.shouldShowUpsellBanner();
  const showSuccessBanner = this.attr(`success-message`) && !this.helpers.shouldShowUpsellBanner();
  const orgId = currentProjectStore.value.organizationId;

  return (
    <ModalEl
      sel='.mp-modal'
      attrs={{
        closeable: true,
        'modal-title': `Share this ${this.helpers.getEntityTypeTitle()} With Others`,
        'modal-description': `Modal for managing sharing permissions`,
        open: this.attr(`open`),
        'hide-close-button': showSuccessBanner,
      }}
    >
      {modalViewType === SharingModalViewType.PublicDashboardDetails ? (
        <PublicDashboardDetailsView
          state={this.state}
          helpers={this.helpers}
          showSuperadminBanner={showSuperadminBanner}
        />
      ) : (
        <div
          sel={`.wrapper`}
          class={{
            'no-min-height': this.helpers.shouldShowUpsellBanner(),
            'with-banner': showDisabledBanner || showSuperadminBanner,
          }}
        >
          {showSuccessBanner ? (
            <div sel='.success-content'>
              <BannerEl
                attrs={{
                  'body-text': this.attr(`success-message`),
                  theme: BannerTheme.Success,
                  type: BannerType.Embedded,
                }}
              />
            </div>
          ) : null}
          <header sel='.modal-header'>{this.t(`components.sharing.title`, {context: this.attr(`type`)})}</header>
          <div sel='.content'>
            {showDisabledBanner ? (
              <BannerEl
                style={{'margin-bottom': `20px`}}
                attrs={{
                  'body-text': this.attr(`disabled-reason`),
                  theme: BannerTheme.Info,
                  type: BannerType.Embedded,
                }}
              />
            ) : null}
            {showSuperadminBanner ? (
              <BannerEl
                style={{'margin-bottom': `20px`}}
                attrs={{
                  'body-text': this.t(`components.sharing.superadmin-access`, {context: this.attr(`type`)}),
                  theme: BannerTheme.Info,
                  type: BannerType.Embedded,
                }}
              />
            ) : null}
            {this.helpers.showExistingSharesSection() ? (
              <>
                {this.helpers.shouldShowUpsellBanner() ? (
                  <>
                    {this.state.upsellInfo ? (
                      <BannerEl
                        attrs={{
                          elref: `upsellBanner`,
                          theme: BannerTheme.Info,
                          type: BannerType.Embedded,
                          'header-text': this.state.upsellInfo.title,
                          'body-text': this.state.upsellInfo.description,
                          ctas: [
                            {
                              text: `Upgrade Plan`,
                              actionName: BannerAction.Upgrade,
                              theme: ButtonTheme.Tertiary,
                              href: `/pricing/plan-builder/?org=${orgId}`,
                            },
                          ],
                        }}
                      />
                    ) : (
                      <BannerEl
                        attrs={{
                          elref: `upsellBanner`,
                          theme: BannerTheme.Info,
                          type: BannerType.Embedded,
                          'body-text': this.t(`components.sharing.upsell-description`, {
                            planName: currentProjectStore.value.eventsPlanName,
                          }),
                          ctas: [
                            {
                              text: this.t(`common.upgrade`),
                              actionName: BannerAction.Upgrade,
                              icon: `share`,
                              theme: ButtonTheme.Tertiary,
                              href: `/pricing`,
                            },
                          ],
                        }}
                      />
                    )}
                    <div
                      sel='.existing-shares'
                      attrs={{elref: `existingShares`}}
                      key='existing-shares'
                      class={{
                        'viewer-state': this.attr(`disabled`),
                      }}
                    >
                      {ShareSection(`People`, null, this.helpers, true)}
                    </div>
                  </>
                ) : (
                  <>
                    <UserSearchTagInputEl
                      params={{
                        tags: this.helpers.picker.getTags(),
                        selectOptions: this.helpers.getSelectOptions(ShareOptionsLocation.PickerControls),
                        disabled: this.attr(`disabled`),
                        placeholder: this.t(`components.sharing.invite-placeholder`),
                        menuSections: this.helpers.picker.getMenuSections(),
                        selectedInputOption: pendingCanEdit
                          ? this.t(`mixology.user-access.editor`)
                          : this.t(`mixology.user-access.viewer`),
                        picker: this.helpers.picker,
                        cta: {
                          text: this.t(`common.share`),
                          disabled: pendingGrants.size === 0,
                          onClick: this.helpers.picker.handleShareButtonClick,
                        },
                        searchText,
                        showPopover,
                      }}
                    />
                    <div
                      sel='.existing-shares'
                      attrs={{elref: `existingShares`}}
                      key='existing-shares'
                      class={{
                        'viewer-state': this.attr(`disabled`),
                      }}
                    >
                      {ShareSection(
                        `People`,
                        this.helpers.existingShares.getUserGrants(),
                        this.helpers,
                        this.attr(`disabled`),
                      )}
                      {ShareSection(
                        `Teams`,
                        this.helpers.existingShares.getTeamGrants(),
                        this.helpers,
                        this.attr(`disabled`),
                      )}
                    </div>
                  </>
                )}
              </>
            ) : (
              <div sel='.placeholder'>
                <span>{this.t(`components.sharing.loading`)}</span>
                <SpinnerEl sel='.mp-spinner' />
              </div>
            )}
          </div>
          {this.helpers.showProjectShareSection() ? (
            <div sel='.share-section--project' attrs={{elref: `shareSectionProject`}}>
              <header sel='.header'>
                {this.helpers.publicDashboard.showPublicDashboardSwitch()
                  ? this.t(`components.sharing.advanced`)
                  : this.t(`components.sharing.project`)}
              </header>
              <ul sel='.share-list'>
                <li sel='.project-share.share-list-item'>
                  <div sel='.display'>
                    <div sel='.name-wrapper'>
                      <div sel='.project-name'>{this.helpers.getProjectShareLabel()}</div>
                      {!this.helpers.shouldShowUpsellBanner() && (
                        <TextHighlightEl
                          sel='.mp-text-highlight'
                          attrs={{
                            theme: TEXT_HIGHLIGHT_THEME.SUCCESS,
                            text: this.helpers.getProjectShareGrantStatus(),
                          }}
                        />
                      )}
                    </div>
                    {this.attr(`disabled`) || this.helpers.publicDashboard.showPublicDashboardSwitch() ? null : (
                      <div sel='.subtitle'>{this.t(`components.sharing.members`, {count: projectMemberCount})}</div>
                    )}
                  </div>
                  <div sel='.controls'>
                    <SelectEl
                      sel='.share-level'
                      attrs={{
                        'disable-search': true,
                        'menu-width': 120,
                        theme: SelectTheme.Tertiary,
                        'always-show-caret': true,
                        'selected-option': this.helpers.getProjectShareSelectOption(),
                        elref: `selectShareLevel_${this.attr(`project-id`)}`,
                        disabled: this.attr(`disabled`),
                      }}
                      params={{
                        sections: this.helpers.getSelectOptions(ShareOptionsLocation.ProjectShare),
                      }}
                      on={{
                        change: this.helpers.handleProjectShareSelectChange,
                      }}
                    />
                  </div>
                </li>
                {this.helpers.publicDashboard.showPublicDashboardSwitch() ? (
                  <li sel='.project-share.share-list-item'>
                    <div sel='.display'>
                      <div
                        sel='.name-wrapper'
                        class={{disabled: !this.helpers.publicDashboard.projectHasFeatureEnabled()}}
                      >
                        <div sel='.project-name'>{this.t(`components.sharing.boards.make-public`)}&nbsp;</div>
                        {this.helpers.hasWorkspacesFeatureGate() ? (
                          <div sel='.workspace-name'>{`via ${truncate(
                            this.helpers.getCurrentWorkspaceName(),
                            60,
                          )}`}</div>
                        ) : null}
                        {!this.helpers.publicDashboard.projectHasFeatureEnabled() && (
                          <>
                            <SvgIconEl attrs={{icon: `info`, elref: `disabledInfoIcon`}} />
                            <TooltipEl
                              attrs={{
                                text: this.t(`components.sharing.boards.share-publicly`, {
                                  link: `/settings/project/${currentProjectStore.value.id}/public-dashboards`,
                                }),
                                placement: Placement.Right,
                                elref: `disabledTextTooltip`,
                                'delay-out': 1000,
                              }}
                            />
                          </>
                        )}
                      </div>
                    </div>
                    <div sel='.controls'>
                      {!this.helpers.publicDashboard.canToggleFreePublicDashboards() &&
                        !this.helpers.projectHasIntegrated() && (
                          <TooltipEl
                            attrs={{
                              text: this.t(`components.sharing.boards.empty-project`),
                              placement: Placement.Top,
                              elref: `disabledToggleTooltip`,
                            }}
                          />
                        )}
                      <SwitchEl
                        sel='.public-dashboard-switch'
                        attrs={{
                          on: publicDashboardEnabled,
                          disabled:
                            (this.helpers.isSharingEnabledForProject() && this.attr(`disabled`)) ||
                            (!this.helpers.isSharingEnabledForProject() &&
                              !this.helpers.publicDashboard.canToggleFreePublicDashboards()) ||
                            !this.helpers.publicDashboard.projectHasFeatureEnabled(),
                          elref: `publicDashboardSwitch`,
                        }}
                        on={{
                          change: this.helpers.publicDashboard.handlePublicDashboardSwitchChange,
                        }}
                      />
                    </div>
                  </li>
                ) : null}
              </ul>
            </div>
          ) : null}
          <footer sel='.modal-actions'>
            <div sel='.copy-link-button-wrapper'>
              <TooltipEl
                attrs={{
                  text: this.t(`components.sharing.url-tooltip`),
                }}
              />
              <ButtonEl
                sel='.copy-link-button'
                attrs={{
                  theme: ButtonTheme.Tertiary,
                  icon: this.helpers.shouldShowCopyLinkSpinner() ? `spinner` : `link`,
                  disabled: this.helpers.shouldShowCopyLinkSpinner(),
                }}
                on={{
                  click: this.helpers.shortUrl.handleCopyLinkClick,
                }}
              >
                {this.t(`common.copy-url`)}
              </ButtonEl>
              {showCopyLinkText ? (
                <input
                  sel='.copy-link-input'
                  attrs={{type: `text`, readonly: true}}
                  hook={{insert: ({elm}) => this.helpers.handleCopyLinkInputInsert(elm)}}
                  on={{click: this.helpers.handleCopyLinkInputClick}}
                />
              ) : null}
            </div>
            {!showCopyLinkText &&
            !this.helpers.publicDashboard.showPublicDashboardSwitch() &&
            !this.helpers.shouldShowUpsellBanner() ? (
              <span sel='.link-info'>{this.t(`components.sharing.url-description`)}</span>
            ) : null}
            {this.helpers.publicDashboard.showPublicDashboardSwitch() &&
            this.helpers.publicDashboard.projectHasFeatureEnabled() &&
            this.helpers.publicDashboard.canToggleFreePublicDashboards() ? (
              <ButtonEl
                sel='.public-dashboard-detail-button'
                attrs={{
                  elref: `publicDashboardDetailsButton`,
                  theme: ButtonTheme.Tertiary,
                  icon: `settings`,
                }}
                on={{
                  click: () =>
                    this.helpers.publicDashboard.updateSharingModalViewType(
                      SharingModalViewType.PublicDashboardDetails,
                    ),
                }}
              >
                {this.t(`components.sharing.boards.public-board-details`)}
              </ButtonEl>
            ) : null}
            {this.helpers.showExistingSharesSection() &&
            !this.helpers.publicDashboard.showPublicDashboardSwitch() &&
            !this.helpers.shouldShowUpsellBanner() ? (
              <ButtonEl
                sel='.share-done-button'
                attrs={{
                  theme: ButtonTheme.Primary,
                }}
                on={{
                  click: () => this.helpers.handleDoneButtonClick(),
                }}
              >
                {this.helpers.t(`common.done`)}
              </ButtonEl>
            ) : null}
          </footer>
        </div>
      )}
    </ModalEl>
  );
}
